import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, AlertTriangle, PlusCircle, RefreshCw, X, Undo2, ImageDown, Table } from "lucide-react";
import { api, adminHeaders } from "../lib/api";

const CHUNK_ROWS = 200;

// Split CSV text into records, honouring quoted fields that contain newlines.
function csvRecords(text) {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (quoted && text[i + 1] === '"') { cur += '""'; i++; continue; }
      quoted = !quoted;
      cur += c;
      continue;
    }
    if (!quoted && (c === "\n" || c === "\r")) {
      if (c === "\r" && text[i + 1] === "\n") i++;
      if (cur.trim()) out.push(cur);
      cur = "";
      continue;
    }
    cur += c;
  }
  if (cur.trim()) out.push(cur);
  return out;
}

const COLUMN_LABEL = {
  cover_url: "cover", cover_focal: "cover focus", media_type: "medium", note: "description",
  audience: "why you should read this", image_url: "image", image_focal: "image focus",
};
const label = (k) => COLUMN_LABEL[k] || k.replace(/_/g, " ");

function Panel({ title, blurb, exportPath, importPath, onDone, coverCapable, chunkable }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { created, updated, errors, replaced, details }
  const [payload, setPayload] = useState(null); // { kind: "json" | "csv", data } to apply on confirm
  const [replaceDup, setReplaceDup] = useState(false); // replace same-title entries instead of adding copies
  const [lastBatch, setLastBatch] = useState(null); // { id, label } — undo target after apply
  const [postImport, setPostImport] = useState(null); // { batch_id, count } — offer covers after a library import
  const [progress, setProgress] = useState(null); // { done, total } while a big import applies
  const slug = title.toLowerCase().replace(/\s+/g, "-");

  const download = async (kind = "json") => {
    try {
      if (kind === "csv") {
        const r = await api.get(`${exportPath}/csv`, { headers: adminHeaders(), responseType: "text" });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}-template.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Sheet downloaded — open it in Google Sheets");
        return;
      }
      const r = await api.get(exportPath, { headers: adminHeaders() });
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-template.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Download failed"); }
  };

  const postImportFile = ({ kind, data }, mode, previewOnly, batchId) => {
    const path = kind === "csv" ? `${importPath}/csv` : importPath;
    const q = `?${previewOnly ? "preview=true&" : ""}on_duplicate=${mode ? "replace" : "add"}${batchId ? `&batch_id=${batchId}` : ""}`;
    const headers = kind === "csv" ? { ...adminHeaders(), "Content-Type": "text/csv" } : adminHeaders();
    return api.post(`${path}${q}`, data, { headers });
  };

  // Big spreadsheets are applied in slices so the browser can report progress and
  // no single request runs long enough to be cut off by the gateway.
  const applyPayload = async () => {
    const rows = payload.kind === "csv" ? csvRecords(payload.data) : [];
    if (!chunkable || payload.kind !== "csv" || rows.length - 1 <= CHUNK_ROWS) {
      setProgress(null);
      const r = await postImportFile(payload, replaceDup, false);
      return r.data;
    }
    const [header, ...body] = rows;
    const total = body.length;
    const totals = { created: 0, updated: 0, errors: 0, replaced: 0, columns: {} };
    let batchId = null;
    let undoUnavailable = false;
    setProgress({ done: 0, total });
    for (let i = 0; i < total; i += CHUNK_ROWS) {
      const slice = body.slice(i, i + CHUNK_ROWS);
      const r = await postImportFile(
        { kind: "csv", data: [header, ...slice].join("\n") },
        replaceDup, false, batchId,
      );
      const d = r.data;
      batchId = batchId || d.batch_id || null;
      undoUnavailable = undoUnavailable || !!d.undo_unavailable;
      totals.created += d.created || 0;
      totals.updated += d.updated || 0;
      totals.errors += d.errors || 0;
      totals.replaced += d.replaced || 0;
      setProgress({ done: Math.min(i + slice.length, total), total });
    }
    return { ...totals, batch_id: batchId, undo_unavailable: undoUnavailable };
  };

  const doPreview = async (next, mode) => {
    setBusy(true);
    try {
      const r = await postImportFile(next, mode, true);
      setPayload(next);
      setPreview(r.data);
      setPostImport(null);
    } catch {
      toast.error("Couldn't read file — check it is a valid JSON or CSV export");
    } finally { setBusy(false); }
  };

  const runPreview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const isCsv = /\.csv$/i.test(file.name) || (!text.trimStart().startsWith("{") && !text.trimStart().startsWith("["));
      await doPreview(isCsv ? { kind: "csv", data: text } : { kind: "json", data: JSON.parse(text) }, replaceDup);
    } catch {
      toast.error("Couldn't read file — check it is a valid JSON or CSV export");
    } finally { e.target.value = ""; }
  };

  // Flip the replace-duplicates mode; re-preview live if a file is already loaded.
  const toggleReplace = async () => {
    const next = !replaceDup;
    setReplaceDup(next);
    if (payload) await doPreview(payload, next);
  };

  const confirmApply = async () => {
    if (!payload) return;
    setBusy(true);
    try {
      const { created = 0, updated = 0, errors = 0, replaced = 0, batch_id, undo_unavailable } = await applyPayload();
      const rep = replaced ? ` (${replaced} replaced)` : "";
      toast.success(`Applied: ${created} added, ${updated} updated${rep}${errors ? `, ${errors} skipped` : ""}`);
      if (undo_unavailable) toast.warning("Applied, but this import couldn't be saved for one-click undo");
      if (batch_id) setLastBatch({ id: batch_id, label: `${created} added, ${updated} updated${rep}` });
      if (coverCapable && batch_id && (created + updated) > 0) setPostImport({ batch_id, count: created + updated });
      setPreview(null);
      setPayload(null);
      onDone?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (!status || status === 502 || status === 504 || e?.code === "ECONNABORTED") {
        toast.error("The connection dropped before the import finished. Re-run the preview to see what still needs applying.");
      } else {
        toast.error(detail ? `Import failed: ${detail}` : `Import failed while applying (${status})`);
      }
    } finally { setBusy(false); setProgress(null); }
  };

  // After a library import, fetch & store covers for just the imported entries.
  const autoCovers = async () => {
    if (!postImport) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/library/fetch-covers", { batch_id: postImport.batch_id, store_local: true }, { headers: adminHeaders() });
      const { updated = 0, not_found = 0, scanned = 0 } = r.data;
      toast.success(`${updated} cover${updated === 1 ? "" : "s"} found · ${not_found} not found (of ${scanned})`);
      setPostImport(null);
      onDone?.();
    } catch { toast.error("Cover lookup failed"); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!lastBatch) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/import/undo", { batch_id: lastBatch.id }, { headers: adminHeaders() });
      const { deleted = 0, restored = 0 } = r.data;
      toast.success(`Undone: ${deleted} removed, ${restored} restored`);
      setLastBatch(null);
      onDone?.();
    } catch {
      toast.error("Undo failed — it may have already been reverted");
      setLastBatch(null);
    } finally { setBusy(false); }
  };

  const cancel = () => { setPreview(null); setPayload(null); };

  const errorRows = (preview?.details || []).filter((d) => d.action === "error");
  const changeRows = (preview?.details || []).filter((d) => d.action !== "error");
  const dupRows = (preview?.details || []).filter((d) => d.action === "add" && d.duplicate);
  const replaceRows = (preview?.details || []).filter((d) => d.replaced);

  return (
    <div className="border border-line bg-ink p-6" data-testid={`iepanel-${slug}`}>
      <h3 className="font-serif text-2xl text-vellum">{title}</h3>
      <p className="mt-2 text-sm text-vellum-muted leading-relaxed">{blurb}</p>
      <div className="mt-6 flex flex-wrap gap-4">
        <button onClick={() => download("csv")} data-testid={`export-csv-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors">
          <Table className="w-4 h-4" /> Sheet (.csv)
        </button>
        <button onClick={() => download("json")} data-testid={`export-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors">
          <Download className="w-4 h-4" /> JSON
        </button>
        <label className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors cursor-pointer">
          <Upload className="w-4 h-4" /> {busy && !preview ? "Reading…" : "Upload & preview"}
          <input type="file" accept=".csv,text/csv,application/json,.json" className="hidden" data-testid={`import-${slug}`} onChange={runPreview} disabled={busy} />
        </label>
      </div>
      <p className="mt-3 text-xs text-vellum-muted leading-relaxed">
        Sheets workflow: download the .csv → in Google Sheets use <span className="text-vellum-muted">File → Import</span> → edit → <span className="text-vellum-muted">File → Download → Comma-separated values</span> → upload it back here. Blank cells leave that field untouched; keep the <span className="text-vellum-muted">id</span> column to update a row, clear it to add a new one.
      </p>

      <label className="mt-4 flex items-center gap-3 cursor-pointer select-none" data-testid={`replace-toggle-${slug}`}>
        <input type="checkbox" checked={replaceDup} onChange={toggleReplace} disabled={busy} className="sr-only peer" />
        <span className="relative w-9 h-5 rounded-full bg-line peer-checked:bg-crimson transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:rounded-full after:bg-vellum after:transition-transform peer-checked:after:translate-x-4" />
        <span className="text-sm text-vellum-muted">
          Replace entries with the same title <span className="text-vellum-faint">(overwrite existing instead of adding a copy)</span>
        </span>
      </label>

      {preview && (
        <div className="mt-6 border border-line bg-black/30 p-5" data-testid={`preview-${slug}`}>
          <div className="flex items-center justify-between">
            <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted">What will change</h4>
            <button onClick={cancel} data-testid={`preview-close-${slug}`} className="text-vellum-muted hover:text-crimson transition-colors"><X className="w-4 h-4" /></button>
          </div>

          <div className="mt-4 flex flex-wrap gap-6 font-mono text-sm">
            <span className="inline-flex items-center gap-2 text-vellum" data-testid={`preview-added-${slug}`}>
              <PlusCircle className="w-4 h-4 text-emerald-400" /> {preview.created} to add
            </span>
            <span className="inline-flex items-center gap-2 text-vellum" data-testid={`preview-updated-${slug}`}>
              <RefreshCw className="w-4 h-4 text-sky-400" /> {preview.updated} to update
            </span>
            {preview.replaced > 0 && (
              <span className="inline-flex items-center gap-2 text-vellum" data-testid={`preview-replaced-${slug}`}>
                <RefreshCw className="w-4 h-4 text-crimson" /> {preview.replaced} replaced
              </span>
            )}
            <span className="inline-flex items-center gap-2 text-vellum" data-testid={`preview-errors-${slug}`}>
              <AlertTriangle className={`w-4 h-4 ${preview.errors ? "text-crimson" : "text-vellum-muted"}`} /> {preview.errors} skipped
            </span>
          </div>

          {Object.keys(preview.columns || {}).length > 0 && (
            <div className="mt-4 border-t border-line pt-4" data-testid={`preview-columns-${slug}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-muted mb-2">Fields this file touches</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(preview.columns)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => (
                    <span key={k} className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-line text-vellum-muted">
                      {label(k)} <span className="text-brass">{n}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {errorRows.length > 0 && (
            <div className="mt-5" data-testid={`preview-error-report-${slug}`}>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-crimson">Skipped rows</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {errorRows.map((d) => (
                  <li key={d.index} className="text-vellum-muted">
                    <span className="text-vellum">Row {d.index + 1} — “{d.title}”:</span> {d.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dupRows.length > 0 && (
            <div className="mt-5 border border-brass/40 bg-brass/5 p-4" data-testid={`preview-duplicate-report-${slug}`}>
              <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brass">
                <AlertTriangle className="w-4 h-4" /> Possible duplicates ({dupRows.length})
              </p>
              <p className="mt-2 text-sm text-vellum-muted">These new rows share a title with an entry that already exists — applying will create a second copy. Turn on “Replace entries with the same title” above to overwrite the existing entry instead.</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {dupRows.map((d) => (
                  <li key={d.index} className="text-vellum-muted">
                    <span className="text-vellum">Row {d.index + 1}:</span> “{d.title}”
                  </li>
                ))}
              </ul>
            </div>
          )}

          {replaceRows.length > 0 && (
            <div className="mt-5 border border-crimson/40 bg-crimson/5 p-4" data-testid={`preview-replace-report-${slug}`}>
              <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-crimson">
                <RefreshCw className="w-4 h-4" /> Will replace ({replaceRows.length})
              </p>
              <p className="mt-2 text-sm text-vellum-muted">These entries already exist with the same title — their existing record will be overwritten with the uploaded version (the entry keeps its place and any reader notes).</p>
              <ul className="mt-2 space-y-1.5 text-sm">
                {replaceRows.map((d) => (
                  <li key={d.index} className="text-vellum-muted">
                    <span className="text-vellum">Row {d.index + 1}:</span> “{d.title}”
                  </li>
                ))}
              </ul>
            </div>
          )}

          {changeRows.length > 0 && (
            <ul className="mt-5 max-h-48 overflow-y-auto space-y-1 text-sm" data-lenis-prevent>
              {changeRows.map((d) => (
                <li key={d.index} className="text-vellum-muted">
                  <span className={`font-mono text-xs uppercase mr-2 ${d.action === "add" ? "text-emerald-400" : d.replaced ? "text-crimson" : "text-sky-400"}`}>{d.replaced ? "replace" : d.action}</span>
                  <span className="text-vellum">{d.title}</span>
                  {d.changes?.length > 0 ? (
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-brass" data-testid={`change-cols-${d.index}`}>
                      {d.changes.map(label).join(", ")}
                    </span>
                  ) : (
                    d.action === "update" && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-vellum-faint">no change</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {progress && (
            <div className="mt-6" data-testid={`import-progress-${slug}`}>
              <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted mb-2">
                <span>Applying…</span>
                <span data-testid={`import-progress-count-${slug}`}>
                  {progress.done.toLocaleString()} / {progress.total.toLocaleString()} rows
                </span>
              </div>
              <div className="h-1 w-full bg-line overflow-hidden">
                <div
                  className="h-full bg-crimson transition-[width] duration-300 ease-out"
                  style={{ width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-4">
            <button onClick={confirmApply} disabled={busy || (preview.created + preview.updated === 0)} data-testid={`preview-confirm-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-crimson text-onaccent hover:bg-vellum hover:text-ink transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {busy
                ? progress
                  ? `Applying… ${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%`
                  : "Applying…"
                : `Confirm & apply (${preview.created + preview.updated})`}
            </button>
            <button onClick={cancel} data-testid={`preview-cancel-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-line text-vellum hover:border-crimson hover:text-crimson transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {postImport && !preview && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-brass/40 bg-brass/5 p-4" data-testid={`postimport-covers-${slug}`}>
          <p className="text-sm text-vellum">
            Import applied. Fetch &amp; save book covers for {postImport.count === 1 ? "this entry" : `these ${postImport.count} entries`} now?
          </p>
          <button onClick={autoCovers} disabled={busy} data-testid={`postimport-covers-btn-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-brass text-brass hover:bg-brass hover:text-ink transition-colors disabled:opacity-40">
            <ImageDown className="w-4 h-4" /> {busy ? "Finding…" : "Auto-fetch covers"}
          </button>
        </div>
      )}

      {lastBatch && !preview && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-crimson/40 bg-crimson/5 p-4" data-testid={`undo-bar-${slug}`}>
          <p className="text-sm text-vellum">
            Last import applied — <span className="text-vellum-muted">{lastBatch.label}</span>. Made a mistake?
          </p>
          <button onClick={undo} disabled={busy} data-testid={`undo-${slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border border-crimson text-crimson hover:bg-crimson hover:text-onaccent transition-colors disabled:opacity-40">
            <Undo2 className="w-4 h-4" /> {busy ? "Undoing…" : "Undo import"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminImportExport({ onDone }) {
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="admin-import-export">
      <Panel
        title="Library"
        blurb="Download the current books & essays as a Google-Sheets-ready spreadsheet (or raw JSON). Edit fields or add new rows, then upload to preview changes before applying. Nothing is auto-deleted."
        exportPath="/admin/export/library"
        importPath="/admin/import/library"
        onDone={onDone}
        coverCapable
        chunkable
      />
      <Panel
        title="Curriculums"
        blurb="Download curriculums as a spreadsheet (one row per step) or raw JSON. Each step has a Core (required), Companion (optional), and Optional item. In the sheet, rows sharing an id/title rebuild that curriculum's modules in row order."
        exportPath="/admin/export/curriculums"
        importPath="/admin/import/curriculums"
        onDone={onDone}
      />
    </div>
  );
}
